import { useState } from 'react';
import type { Address, Hash } from 'viem';
import { useWalletClient } from 'wagmi';

import type { TransactionQueueItem } from 'components';
import {
  getContractAddress,
  isolatedPoolComptrollerAbi,
  positionRouterAbi,
  vBep20Abi,
} from 'libs/contracts';
import { wsTAOAbi } from 'libs/contracts/abis/bittensorWrappers';
import { wrapPositionRouterError } from 'libs/errors';
import { useAccountAddress, useChainId, usePublicClient } from 'libs/wallet';
import type { VToken } from 'types';

import {
  failedStepStateReducer,
  getIsRouterDelegated,
  getRouterQueueStep,
  runRouterQueueStep,
} from '../shared/routerQueueCore';
import { invalidateRouterQueries } from '../shared/routerQueueInvalidation';
import {
  calculateSlippageFloor,
  computeNativeOutFloor,
  computeRouterDeadline,
} from '../shared/routerSlippage';

export interface RouterBorrowQueuePlanStep extends TransactionQueueItem {
  shouldSkip: boolean;
}

interface ExecuteRouterBorrowQueueInput {
  amountMantissa: bigint;
  receiveNative: boolean;
  slippagePercentage: number;
  vToken: VToken;
}

export const useRouterBorrowQueue = () => {
  const { accountAddress } = useAccountAddress();
  const { chainId } = useChainId();
  const { publicClient } = usePublicClient();
  const { data: walletClient } = useWalletClient({ account: accountAddress });
  const [items, setItems] = useState<TransactionQueueItem[]>([]);
  const [isRunning, setIsRunning] = useState(false);

  const updateItem = (id: string, status: TransactionQueueItem['status']) => {
    setItems((currentItems) =>
      currentItems.map((item) => (item.id === id ? { ...item, status } : item)),
    );
  };

  const runStep = ({
    action,
    ...step
  }: RouterBorrowQueuePlanStep & { action: () => Promise<Hash> }) =>
    runRouterQueueStep({ step, action, updateItem, publicClient });

  const execute = async ({
    amountMantissa,
    receiveNative,
    slippagePercentage,
    vToken,
  }: ExecuteRouterBorrowQueueInput) => {
    if (!accountAddress || !walletClient) {
      throw new Error('Wallet is not connected');
    }

    const routerAddress = getContractAddress({ name: 'PositionRouter', chainId });
    const poolComptrollerAddress = getContractAddress({
      name: 'LegacyPoolComptroller',
      chainId,
    });

    if (!routerAddress || !poolComptrollerAddress) {
      throw new Error('PositionRouter or LegacyPoolComptroller address not configured');
    }

    setItems([]);
    setIsRunning(true);

    try {
      const symbol = vToken.underlyingToken.symbol;

      const isDelegated = await getIsRouterDelegated({
        accountAddress,
        publicClient,
        poolComptrollerAddress: poolComptrollerAddress as Address,
        routerAddress: routerAddress as Address,
      });

      const plan: RouterBorrowQueuePlanStep[] = [
        {
          id: 'approve-delegate',
          label: 'Approve PositionRouter as delegate',
          shouldSkip: !!isDelegated,
          status: 'idle',
        },
        {
          id: 'router-borrow',
          label: `Borrow ${symbol}`,
          shouldSkip: false,
          status: 'idle',
        },
      ];
      setItems(plan);

      await runStep({
        ...getRouterQueueStep(plan, 'approve-delegate'),
        action: () =>
          walletClient.writeContract({
            address: poolComptrollerAddress as Address,
            abi: isolatedPoolComptrollerAbi,
            functionName: 'updateDelegate',
            args: [routerAddress as Address, true],
            account: accountAddress,
          }),
      });

      const availableCash = await publicClient.readContract({
        address: vToken.address,
        abi: vBep20Abi,
        functionName: 'getCash',
      });
      if (amountMantissa > availableCash) {
        throw new Error(
          `Borrow exceeds available liquidity. Market has ${availableCash} base units available, requested ${amountMantissa}.`,
        );
      }

      // borrowTAO's minTaoOut is checked against post-unwrap native TAO inside
      // wsTAO.unwrapTo, so rescale via exchangeRate() to make the floor apply
      // to what the user actually receives. Plain `borrow` keeps wrapper units.
      let minOut: bigint;
      if (receiveNative) {
        const wsTaoRate = await publicClient.readContract({
          address: vToken.underlyingToken.address,
          abi: wsTAOAbi,
          functionName: 'exchangeRate',
        });
        minOut = computeNativeOutFloor({
          wrapperAmount: amountMantissa,
          exchangeRate: wsTaoRate,
          slippagePercentage,
        });
      } else {
        minOut = calculateSlippageFloor({
          amount: amountMantissa,
          slippagePercentage,
        });
      }
      const deadline = computeRouterDeadline();

      await runStep({
        ...getRouterQueueStep(plan, 'router-borrow'),
        action: () =>
          receiveNative
            ? walletClient.writeContract({
                address: routerAddress as Address,
                abi: positionRouterAbi,
                functionName: 'borrowTAO',
                args: [vToken.address, amountMantissa, minOut, deadline],
                account: accountAddress,
              })
            : walletClient.writeContract({
                address: routerAddress as Address,
                abi: positionRouterAbi,
                functionName: 'borrow',
                args: [vToken.address, amountMantissa, minOut, deadline],
                account: accountAddress,
              }),
      });

      invalidateRouterQueries({
        accountAddress,
        chainId,
        poolComptrollerAddress: poolComptrollerAddress as Address,
        routerAddress: routerAddress as Address,
        vTokenAddresses: [vToken.address],
        tokenAddress: vToken.underlyingToken.address,
      });
    } catch (error) {
      setItems(failedStepStateReducer);
      throw wrapPositionRouterError(error);
    } finally {
      setIsRunning(false);
    }
  };

  return { execute, isRunning, items };
};
